/**
 * <count-up> — Web Component
 *
 * Attributes:
 *   data-number    {number}  required
 *   data-duration  {number}  2500 (ms)
 *   data-suffix    {string}  '+'
 *   data-label     {string}  ''
 *   data-threshold {number}  0.4
 *
 * CSS classes:
 *   count-up .cu-row     — flex row
 *   count-up .cu-number  — animated number
 *   count-up .cu-suffix  — suffix (adds .cu-done on complete)
 *   count-up .cu-label   — label
 */

class CountUpElement extends HTMLElement {
  static get observedAttributes() {
    return ['data-number', 'data-duration', 'data-suffix', 'data-label', 'data-threshold'];
  }

  connectedCallback() {
    this._started = false;
    this._frame = null;
    this._render();
    this._observe();
  }

  disconnectedCallback() {
    cancelAnimationFrame(this._frame);
    this._observer?.disconnect();
  }

  attributeChangedCallback() {
    if (this.isConnected) {
      cancelAnimationFrame(this._frame);
      this._observer?.disconnect();
      this._started = false;
      this._render();
      this._observe();
    }
  }

  get _opts() {
    return {
      number:    parseFloat(this.dataset.number)    || 0,
      duration:  parseFloat(this.dataset.duration)  || 2500,
      suffix:    this.dataset.suffix  ?? '+',
      label:     this.dataset.label   ?? '',
      threshold: parseFloat(this.dataset.threshold) || 0.4,
    };
  }

  _render() {
    const { number, suffix, label } = this._opts;
    // Width is locked to the final comma-formatted string ("1,093") for
    // the whole animation, so the box never resizes - not even when the
    // count crosses a thousand and a comma appears partway through.
    const finalText = Math.round(number).toLocaleString();

    this.innerHTML = `
      <div class="cu-row">
        <span class="cu-number" style="display:inline-block;width:${finalText.length}ch;text-align:right;">0</span><span class="cu-suffix">${suffix}</span>
      </div>
      ${label ? `<div class="cu-label">${label}</div>` : ''}
    `;

    this._numEl = this.querySelector('.cu-number');
    this._sufEl = this.querySelector('.cu-suffix');
  }

  _observe() {
    this._observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        this._start();
        this._observer.disconnect();
      }
    }, { threshold: this._opts.threshold });
    this._observer.observe(this);
  }

  _start() {
    if (this._started) return;
    this._started = true;
    const { number, duration } = this._opts;
    const easeOut = (t) => 1 - Math.pow(1 - t, 4);
    let startTime = null;

    const tick = (now) => {
      if (!startTime) startTime = now;
      const t = Math.min((now - startTime) / duration, 1);
      const val = number * easeOut(t);

      if (t < 1) {
        this._numEl.textContent = Math.round(val).toLocaleString();
        this._frame = requestAnimationFrame(tick);
      } else {
        this._numEl.textContent = Math.round(number).toLocaleString();
        this._sufEl.classList.add('cu-done');
        this.dispatchEvent(new CustomEvent('countup:complete', { bubbles: true }));
      }
    };

    this._frame = requestAnimationFrame(tick);
  }
}

customElements.define('count-up', CountUpElement);

class DropHintNote extends HTMLElement {
  static get observedAttributes() {
    return ['data-id'];
  }

  constructor() {
    super();
    this._saving = false;
    this._statusTimeout = null;
  }

  connectedCallback() {
    this._itemKey = this.dataset.id;
    this._textarea = this.querySelector('textarea[name="itemnote"]');
    this._button = this.querySelector('button');

    if (!this._textarea || !this._button) return;

    this._originalValue = this._textarea.value.trim();

    this._textarea.addEventListener('input', () => this._onInput());
    this._button.addEventListener('click', () => this._onSave());
    this._textarea.addEventListener('keydown', (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') this._onSave();
    });

    this._updateButtonState();
  }

  disconnectedCallback() {
    clearTimeout(this._statusTimeout);
  }

  // ─── Internal ────────────────────────────────────────────────────────────────

  _onInput() {
    this._updateButtonState();
    this._clearStatus();
  }

  _updateButtonState() {
    if (!this._button || !this._textarea) return;
    const isDirty = this._textarea.value.trim() !== this._originalValue;
    this._button.disabled = !isDirty || this._saving;
  }

  async _onSave() {
    if (this._saving || !this._itemKey) return;

    const message = this._textarea.value.trim();
    this._setSaving(true);

    try {
      // 1. Fetch current cart to get all existing line item properties
      const cartRes = await fetch('/cart.js');
      if (!cartRes.ok) throw new Error(`Cart fetch failed: ${cartRes.status}`);
      const cart = await cartRes.json();

      const lineItem = cart.items.find((item) => item.key === this._itemKey);
      if (!lineItem) throw new Error(`Line item "${this._itemKey}" not found in cart`);

      // 2. Merge: keep all existing properties, patch only HintMesage
      const mergedProperties = { ...lineItem.properties };

      if (message) {
        mergedProperties.HintMesage = message;
      } else {
        // Remove the key entirely when blank so Shopify doesn't store an empty string
        delete mergedProperties.HintMesage;
      }

      // 3. POST full merged properties back to cart
      const formData = new FormData();
      formData.append('id', this._itemKey);

      for (const [key, val] of Object.entries(mergedProperties)) {
        formData.append(`properties[${key}]`, val ?? '');
      }

      // Edge case: if mergedProperties is empty, send an explicit empty object
      // so Shopify clears all properties rather than leaving stale ones
      if (Object.keys(mergedProperties).length === 0) {
        formData.append('properties', '');
      }

      const updateRes = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });

      if (!updateRes.ok) throw new Error(`Cart update failed: ${updateRes.status}`);

      const updatedCart = await updateRes.json();

      this._originalValue = message;
      this._updateButtonState();
      //this._setStatus('success', 'Note saved');

      this.dispatchEvent(
        new CustomEvent('hint:saved', {
          bubbles: true,
          detail: {
            id: this._itemKey,
            message,
            properties: mergedProperties,
            cart: updatedCart,
          },
        })
      );
      
    } catch (err) {
      console.error('[drop-hint-note]', err);
      //this._setStatus('error', 'Failed to save. Try again.');

      this.dispatchEvent(
        new CustomEvent('hint:error', {
          bubbles: true,
          detail: { id: this._itemKey, error: err },
        })
      );
    } finally {
      this._setSaving(false);
      window.location.reload(true);
    }
  }

  _setSaving(isSaving) {
    this._saving = isSaving;
    if (!this._button || !this._textarea) return;

    this._textarea.disabled = isSaving;
    this._button.disabled = isSaving;
    this._button.setAttribute('aria-busy', String(isSaving));
    this._button.textContent = isSaving ? 'Saving…' : 'Save';
  }

  _setStatus(type, text) {
    this._clearStatus();

    const el = document.createElement('span');
    el.className = `drop-hint-status drop-hint-status--${type}`;
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    el.textContent = text;

    this._button.insertAdjacentElement('afterend', el);
    this._statusTimeout = setTimeout(() => el.remove(), 3500);
  }

  _clearStatus() {
    clearTimeout(this._statusTimeout);
    this.querySelector('.drop-hint-status')?.remove();
  }

  // ─── Public API ───────────────────────────────────────────────────────────────

  /** Current textarea value */
  get value() {
    return this._textarea?.value ?? '';
  }

  /** Programmatically set textarea value and refresh button state */
  set value(val) {
    if (this._textarea) {
      this._textarea.value = val;
      this._updateButtonState();
    }
  }

  /** The Shopify line item key (data-id) */
  get itemKey() {
    return this._itemKey;
  }

  /** Manually trigger a save (e.g. on cart form submit) */
  save() {
    return this._onSave();
  }
}

customElements.define('drop-hint-note', DropHintNote);

document.addEventListener("DOMContentLoaded", (event) => {
    extraProductsEvents();

    document.querySelectorAll(".item-remove").forEach((btn) => {
      btn.addEventListener("click", async (evt) => {
        const checkoutButton = document.querySelector(".cart__checkout-button");
       checkoutButton.classList.add("loading");
       checkoutButton.setAttribute('aria-disabled', true);
          checkoutButton.querySelector(".loading__spinner").classList.remove("hidden");
        const item = {
          id:btn.dataset.key,
          quantity:0
        };
        fetch(window.Shopify.routes.root + 'cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(item)
        })
        .then(async (response) => {
          // If removing this item dropped the cart below the free gift
          // threshold (or above it), sync the gift line in this same
          // action before reloading - avoids a second reload just for that.
          if (window.mdvSyncFreeGift) await window.mdvSyncFreeGift();
          window.location.reload(true);
        })
        .catch(async (error) => {
          if (window.mdvSyncFreeGift) await window.mdvSyncFreeGift();
          window.location.reload(true);
        });
      });
    });
    document.querySelectorAll(".item-view").forEach((btn) => {
      btn.addEventListener("click",(evt) => {
        window.location = btn.dataset.href;
      });
    });
    document.querySelectorAll(".cart-ring-size").forEach((select) => {
      select.addEventListener("change", async (evt) => {
        const size = select.value;
        const itemKey = select.dataset.key;

        try {
          // Step 1: Fetch current cart to get existing properties
          const cartRes = await fetch("/cart.js");
          const cart = await cartRes.json();

          // Step 2: Find the matching line item by key
          const lineItem = cart.items.find((item) => item.key === itemKey);

          if (!lineItem) {
            console.error("Line item not found for key:", itemKey);
            return;
          }
          const checkoutButton = document.querySelector(".cart__checkout-button");
          checkoutButton.classList.add("loading");
          checkoutButton.setAttribute('aria-disabled', true);
          checkoutButton.querySelector(".loading__spinner").classList.remove("hidden");
          // Step 3: Merge new ring_size into existing properties
          const updatedProperties = {
            ...lineItem.properties,  // preserve all existing properties
            ring_size: size,         // update only ring_size
          };

          // Step 4: POST merged properties back to Shopify
          const updateRes = await fetch(window.Shopify.routes.root + 'cart/change.js', {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              id: itemKey,                        // use item key (string), not variant ID
              quantity: lineItem.quantity,        // required — keep current quantity
              properties: updatedProperties,
            }),
          });

          const updatedCart = await updateRes.json();
          console.log("Cart updated:", updatedCart);
           window.location.reload(true);

        } catch (err) {
          console.error("Failed to update ring size:", err);
           window.location.reload(true);
        }
      });
    });
});
document.addEventListener('product-modal:loaded', (event) => {
  console.log('Product modal loaded!', event.detail);
  extraProductsEvents();
});
function formatMoney(cents, format = "${{amount}}") {
  if (typeof cents === "string") {
    cents = cents.replace(".", "");
  }

  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

  const formatWithDelimiters = (number, precision = 2, thousands = ",", decimal = ".") => {
    number = (number / 100).toFixed(precision);

    const parts = number.split(".");
    const dollars = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    const centsPart = parts[1] ? decimal + parts[1] : "";

    return dollars + centsPart;
  };

  return format.replace(placeholderRegex, (match, key) => {
    switch (key) {
      case "amount":
        return formatWithDelimiters(cents, 2);
      case "amount_no_decimals":
        return formatWithDelimiters(cents, 0);
      case "amount_with_comma_separator":
        return formatWithDelimiters(cents, 2, ".", ",");
      case "amount_no_decimals_with_comma_separator":
        return formatWithDelimiters(cents, 0, ".", ",");
      default:
        return formatWithDelimiters(cents, 2);
    }
  });
}
function removeEmojis(text){
    return text.replace(/[\p{Emoji_Presentation}\p{Extended_Pictographic}]/gu,"");
}
subscribe(PUB_SUB_EVENTS.variantChange, (evt) => {
  updateProductTotalElement();
});
const updateProductTotalElement = () => {
  const mainProductWrappers = document.querySelectorAll(".product__info-wrapper");
  if(mainProductWrappers.length > 0){
    mainProductWrappers.forEach((mainWrapper) => {
      const priceElement = mainWrapper.querySelector(".mdv-pdp-product-total-wrapper .total-value");
      if (!priceElement) return;

      let basePrice = Number(priceElement.dataset.oprice) || 0;

      const extras = mainWrapper.querySelectorAll(
        ".mdv-extra-products-block-item.active-extra .active"
      );

      const extrasTotal = [...extras].reduce((sum, el) => {
        return sum + (Number(el.dataset.price) || 0);
      }, 0);
      const extraApcs = mainWrapper.querySelectorAll(".mdv-pdp-apc-product-item input:checked");
      const extrasApcTotal = [...extraApcs].reduce((sum, el) => {
        return sum + (Number(el.dataset.price) || 0);
      }, 0);
      const total = basePrice + extrasTotal + extrasApcTotal;

      // Update UI (if needed)
      priceElement.textContent = formatMoney(total,theme.moneyWithCurrencyFormat);
      updatePromobar(total);
    });
  }

  
};
const updatePromobar = async (selectedAmount) => {
  const promoFill = document.getElementById('promo-fill');
  const promoAway = document.getElementById('promo-away');
  const promoBar = document.querySelector(".promo-bar-wrapper");
  

  const promoGoal = (parseInt(promoBar.dataset.threshold) * parseFloat(Shopify.currency.rate));
   const cartRes = await fetch("/cart.js");
  const cart = await cartRes.json();
  const cart_total = cart.total_price;
  const main_amount = cart_total + selectedAmount;
  promoBar.querySelector(".t-amount").textContent = formatMoney(promoGoal,theme.moneyWithCurrencyFormat)
  if(main_amount >= promoGoal) {
    promoFill.style.width = '100%';
    promoFill.style.background = '#054b5d';
    promoAway.innerHTML = '<span style="color:#054b5d;">You unlocked Free Diamond Studs! 🎉</span>';
  } else {
    const percent = (main_amount / promoGoal) * 100;
    const away = promoGoal - main_amount;
    promoFill.style.width = percent + '%';
    promoFill.style.background = '#054b5d';
    promoAway.innerHTML = `Add <span class="remaining-amount">${formatMoney(away,theme.moneyWithCurrencyFormat)}</span> to unlock`;
  }
}
const extraProductsEvents = () => {
    const engravingInput = document.querySelector('.pdp-engraving-input');
    if(engravingInput){
        // Handle typing
        engravingInput.addEventListener('input', function () {
            this.value = removeEmojis(this.value);
        });
    }
    document.querySelectorAll(".mdv-extra-products-block-item-action .extra-add, .mdv-extra-products-block-item-action .extra-remove").forEach((button) => {
        button.addEventListener("click", () => {
            const isAdd = button.classList.contains("extra-add");
            const itemsWrapper = button.closest(".mdv-extra-products-block-items");
            const itemWrapper = button.closest(".mdv-extra-products-block-item");
            const actionWrapper = button.closest(".mdv-extra-products-block-item-action");

            itemsWrapper.querySelectorAll(".mdv-extra-products-block-item").forEach((item) => {
                item.classList.toggle("op-5", isAdd && item !== itemWrapper);
                item.classList.toggle("active-extra", isAdd && item === itemWrapper);
                item.querySelectorAll("button.button").forEach((btn) => btn.disabled = isAdd && item !== itemWrapper);
            });

            actionWrapper.querySelector(".extra-add").classList.toggle("hidden", isAdd);
            actionWrapper.querySelector(".extra-add").classList.toggle("active", isAdd);
            actionWrapper.querySelector(".extra-remove").classList.toggle("hidden", !isAdd);
            updateProductTotalElement();
        });
    });
    document.querySelectorAll(".custom-product-back-types-item input").forEach((input) => {
      input.addEventListener("change",(evt) => {
        const mainElement = input.closest(".custom-product-backtypes-block");
        console.log(mainElement,"mainElementmainElement");
        if(mainElement && mainElement.querySelector(".product-swatch-selected-name")){
          mainElement.querySelector(".product-swatch-selected-name").textContent = input.value;
        }
      });
    });
    document.querySelectorAll(".mdv-pdp-apc-product-item input").forEach((input) => {
      input.addEventListener("change",(evt) => {
        console.log("event change hee");
        updateProductTotalElement();
      });
    });
    updateProductTotalElement();
}

//window.addEventListener('load', function () {
document.addEventListener("DOMContentLoaded",(evt)=>{
  setTimeout(function () {
    document.querySelectorAll('header-menu details.mega-menu').forEach(function (details) {
      var timeout;

      details.addEventListener('mouseenter', function () {
        clearTimeout(timeout);
        document.querySelectorAll('header-menu details.mega-menu[open]').forEach(function (other) {
          if (other !== details) other.removeAttribute('open');
        });
        details.setAttribute('open', '');
      });

      details.addEventListener('mouseleave', function () {
        timeout = setTimeout(function () {
          details.removeAttribute('open');
        }, 150);
      });
    });
  }, 500);
});


function updateSwatchImage(cardWrapper, swatchImage,swatchImage2) {
  const secondImg = cardWrapper.querySelector(".second-image");
  const firstImage = cardWrapper.querySelector(".first-image");
  if (!swatchImage) return;

  // Build srcset using the same widths Shopify uses
  const widths = [375, 750, 1100, 1500, 1780, 2000];
  const baseUrl = swatchImage.split("?")[0]; // strip existing query params
  
  const newSrcset = widths
    .map((w) => `${baseUrl}?width=${w} ${w}w`)
    .join(", ");
  
  
  if(firstImage){
    //firstImage.removeAttribute("loading");
    //firstImage.removeAttribute("fetchpriority");
    //firstImage.removeAttribute("decoding");
    firstImage.srcset = newSrcset;
    firstImage.src = swatchImage;

    // Force lazy-loaded image to load
    firstImage.loading = "lazy";
    
  }
  if (!swatchImage2) return;
  if(secondImg){
    const baseUrl2 = swatchImage2.split("?")[0]; // strip existing query params
  const newSrcset2 = widths
    .map((w) => `${baseUrl2}?width=${w} ${w}w`)
    .join(", ");
    //secondImg.removeAttribute("loading");
    //secondImg.removeAttribute("fetchpriority");
    //secondImg.removeAttribute("decoding");
    secondImg.srcset = newSrcset2;
    secondImg.src = newSrcset2;

    // Force lazy-loaded image to load
    secondImg.loading = "lazy";
  }
  
}

function initSwatches() {
  document.querySelectorAll(".mdv-collection-product-metal-swatch").forEach((swatch) => {
    // Prevent duplicate listeners
    swatch.replaceWith(swatch.cloneNode(true));
  });

  document.querySelectorAll(".mdv-collection-product-metal-swatch").forEach((swatch) => {
    swatch.addEventListener("click", (evt) => {
      evt.preventDefault();

      const cardWrapper = swatch.closest(".card-wrapper");
      if (!cardWrapper) return;

      // Remove active from all swatches in this card
      cardWrapper
        .querySelectorAll(".mdv-collection-product-metal-swatch")
        .forEach((el) => el.classList.remove("active"));

      swatch.classList.add("active");
      updateSwatchImage(cardWrapper, swatch.dataset.image,swatch.dataset.image2);
    });
  });
  /*document.querySelectorAll(".card-wrapper").forEach((card, index) => {
    const firstSwatch = card.querySelector(".mdv-collection-product-metal-swatch.active");
    if (!firstSwatch) return;

    setTimeout(() => {
      firstSwatch.click();
    }, index * 10);
  });*/
}
//window.addEventListener('load', initSwatches);
document.addEventListener("DOMContentLoaded",initSwatches);
(function () {
  const state = {};

  function slideDown(el, dur, key) {
    el.style.display = 'block';
    const h = el.scrollHeight;
    el.style.height = '0px';
    el.style.opacity = '0';
    const t0 = performance.now();
    (function tick(now) {
      const p = Math.min((now - t0) / dur, 1);
      const e = 1 - Math.pow(1 - p, 3);
      el.style.height = (h * e) + 'px';
      el.style.opacity = e;
      p < 1
        ? requestAnimationFrame(tick)
        : (el.style.height = el.style.opacity = '', state[key].anim = false);
    })(t0);
  }

  function slideUp(el, dur, key) {
    const h = el.scrollHeight;
    el.style.height = h + 'px';
    el.style.opacity = '1';
    const t0 = performance.now();
    (function tick(now) {
      const p = Math.min((now - t0) / dur, 1);
      const e = 1 - Math.pow(1 - p, 3);
      el.style.height = (h * (1 - e)) + 'px';
      el.style.opacity = (1 - e);
      if (p < 1) {
        requestAnimationFrame(tick);
      } else {
        el.style.display = 'none';
        el.style.height = el.style.opacity = '';
        state[key].anim = false;
      }
    })(t0);
  }

  function attachToggle(triggerSelector, contentSelector, prefix) {
    document.querySelectorAll(triggerSelector).forEach(function (trigger) {
      const id = trigger.dataset.id;
      const key = prefix + ':' + id; // ← namespaced key, no more collisions
      const content = document.querySelector(
        contentSelector + '[data-id="' + id + '"]'
      );
      if (!content) return;

      state[key] = { open: false, anim: false };

      trigger.addEventListener('click', function () {
        const s = state[key];
        if (s.anim) return;
        s.anim = true;
        if (!s.open) {
          slideDown(content, 320, key);
        } else {
          slideUp(content, 280, key);
        }
        s.open = !s.open;
      });
    });
  }

  attachToggle(
    '.product-action-link',
    '.mdv-cart-item-match-products-content',
    'product'
  );

  attachToggle(
    '.note-action-link',
    '.mdv-cart-item-note-form-wrapper',
    'note'
  );
})();

const btn = document.getElementById("backToTop");

function toggleButton() {
  const scrollTop = window.scrollY;
  const docHeight = document.documentElement.scrollHeight - window.innerHeight;

  if (scrollTop / docHeight > 0.75) {
    btn.classList.add("show");
  } else {
    btn.classList.remove("show");
  }
}

window.addEventListener("scroll", toggleButton);

btn.addEventListener("click", () => {
  window.scrollTo({
    top: 0,
    behavior: "smooth"
  });
});

document.addEventListener('DOMContentLoaded', function () {
  document.querySelectorAll('.open-chat-box')
    .forEach((el) => {
        el.addEventListener("click",()=>{
          console.log("dddd");
          document.querySelector('#ShopifyChat').shadowRoot.querySelector('button').click();
        })
    });
});